import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaHeart, FaTrash, FaShoppingCart, FaArrowLeft } from 'react-icons/fa';
import { collection, query, where, getDocs, doc, deleteDoc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import MarketplaceLayout from './MarketplaceLayout';
import { SEOHead } from '../SEO';

const Container = styled.div`
  color: white;
`;

const Header = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 2rem;
  border-radius: 15px;
  margin-bottom: 2rem;
  position: relative;
`;

const Title = styled.h1`
  font-size: 2rem;
  text-align: center;
  margin-bottom: 1rem;

  &:after {
    content: '';
    display: block;
    width: 60px;
    height: 4px;
    background: #4a90e2;
    margin: 1rem auto;
    border-radius: 2px;
  }
`;

const WishlistGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 2rem;
`;

const WishlistItem = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

const ItemImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  cursor: pointer;
`;

const ItemInfo = styled.div`
  padding: 1.5rem;
`;

const ItemTitle = styled.h3`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  
  &:hover {
    color: #4a90e2;
  }
`;

const ItemPrice = styled.div`
  color: #4a90e2;
  font-weight: 600;
  font-size: 1.2rem;
  margin-bottom: 1rem;
`;

const ItemActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled(motion.button)`
  background: ${props => props.primary ? '#4a90e2' : 'rgba(255, 255, 255, 0.1)'};
  color: white;
  border: none;
  padding: 0.5rem;
  border-radius: 5px;
  flex: ${props => props.flex || '1'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.primary ? '#357abd' : 'rgba(255, 255, 255, 0.2)'};
  }
`;

const RemoveButton = styled(motion.button)`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: rgba(0, 0, 0, 0.5);
  color: #ff4d4d;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
  
  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
`;

const EmptyWishlist = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
`;

const EmptyWishlistIcon = styled.div`
  font-size: 3rem;
  color: #4a90e2;
  margin-bottom: 1rem;
`;

const EmptyWishlistMessage = styled.p`
  margin-bottom: 2rem;
  font-size: 1.2rem;
  color: #ccc;
`;

const ContinueShoppingButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 1rem;
  margin: 0 auto;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const Wishlist = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentUser } = useAuth();
  const { addToCart } = useCart();
  
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (currentUser) {
      fetchWishlistItems();
    } else {
      setLoading(false);
      setWishlistItems([]);
    }
  }, [currentUser]);
  
  const fetchWishlistItems = async () => {
    try {
      setLoading(true);
      
      // Get user's wishlist
      const wishlistRef = collection(db, 'wishlists');
      const q = query(wishlistRef, where('userId', '==', currentUser.uid));
      const wishlistSnapshot = await getDocs(q);
      
      if (wishlistSnapshot.empty) {
        setWishlistItems([]);
        setLoading(false);
        return;
      }
      
      // Get the first wishlist document (users should only have one)
      const wishlistDoc = wishlistSnapshot.docs[0];
      const wishlistData = wishlistDoc.data();
      
      // Fetch product details for each item in the wishlist
      const productPromises = wishlistData.items.map(async (itemId) => {
        const productRef = doc(db, 'products', itemId);
        const productSnap = await getDoc(productRef);
        
        if (productSnap.exists()) {
          return {
            ...productSnap.data(),
            $id: productSnap.id,
            wishlistId: wishlistDoc.id
          };
        }
        
        return null;
      });
      
      const products = await Promise.all(productPromises);
      
      // Filter out any null values (products that no longer exist)
      setWishlistItems(products.filter(product => product !== null));
      
    } catch (err) {
      setError(err.message);
      toast.error('Error fetching wishlist: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleRemoveFromWishlist = async (item) => {
    try {
      // Get the current wishlist
      const wishlistRef = doc(db, 'wishlists', item.wishlistId);
      const wishlistSnap = await getDoc(wishlistRef);
      
      if (!wishlistSnap.exists()) {
        throw new Error('Wishlist not found');
      }
      
      const wishlistData = wishlistSnap.data();
      
      // Remove the item from the items array
      const updatedItems = wishlistData.items.filter(id => id !== item.$id);
      
      // Update the wishlist document
      await updateDoc(wishlistRef, {
        items: updatedItems
      });
      
      // Update local state
      setWishlistItems(wishlistItems.filter(product => product.$id !== item.$id));
      
      toast.success(`${item.name} removed from wishlist`);
      
    } catch (err) {
      toast.error('Error removing from wishlist: ' + err.message);
    }
  };
  
  const handleAddToCart = (item) => {
    addToCart({
      id: item.$id,
      title: item.name,
      price: item.price,
      imageUrl: item.imageUrl,
      type: 'product',
      sellerId: item.sellerId,
      quantity: 1
    });
    
    toast.success(`${item.name} added to cart`);
  };
  
  const handleViewDetails = (item) => {
    navigate(`/marketplace/products/${item.$id}`);
  };
  
  if (loading) {
    return (
      <MarketplaceLayout>
        <Container>
          <Header>
            <Title>My Wishlist</Title>
          </Header>
          <p>Loading wishlist...</p>
        </Container>
      </MarketplaceLayout>
    );
  }
  
  if (error) {
    return (
      <MarketplaceLayout>
        <Container>
          <Header>
            <Title>My Wishlist</Title>
          </Header>
          <p>Error: {error}</p>
        </Container>
      </MarketplaceLayout>
    );
  }
  
  if (!currentUser) {
    return (
      <MarketplaceLayout>
        <Container>
          <Header>
            <Title>My Wishlist</Title>
          </Header>
          <EmptyWishlist>
            <EmptyWishlistMessage>Please sign in to view your wishlist</EmptyWishlistMessage>
            <ContinueShoppingButton
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/login')}
            >
              Sign In
            </ContinueShoppingButton>
          </EmptyWishlist>
        </Container>
      </MarketplaceLayout>
    );
  }
  
  if (wishlistItems.length === 0) {
    return (
      <MarketplaceLayout>
        <Container>
          <SEOHead
            title="My Wishlist"
            description="View and manage your saved items on Konzo Marketplace"
            canonicalUrl="/marketplace/wishlist"
          />
          <Header>
            <Title>My Wishlist</Title>
          </Header>
          <EmptyWishlist>
            <EmptyWishlistIcon>
              <FaHeart />
            </EmptyWishlistIcon>
            <EmptyWishlistMessage>Your wishlist is empty</EmptyWishlistMessage>
            <ContinueShoppingButton
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/marketplace')}
            >
              <FaArrowLeft /> Continue Shopping
            </ContinueShoppingButton>
          </EmptyWishlist>
        </Container>
      </MarketplaceLayout>
    );
  }
  
  return (
    <MarketplaceLayout>
      <Container>
        <SEOHead
          title="My Wishlist"
          description="View and manage your saved items on Konzo Marketplace"
          canonicalUrl="/marketplace/wishlist"
        />
        <Header>
          <Title>My Wishlist</Title>
        </Header>
        
        <WishlistGrid>
          <AnimatePresence>
            {wishlistItems.map(item => (
              <WishlistItem
                key={item.$id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                layout
              >
                <RemoveButton
                  onClick={() => handleRemoveFromWishlist(item)}
                  whileTap={{ scale: 0.9 }}
                >
                  <FaTrash />
                </RemoveButton>
                
                <ItemImage 
                  src={item.imageUrl} 
                  alt={item.name}
                  onClick={() => handleViewDetails(item)}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/placeholder-product.jpg';
                  }}
                />
                
                <ItemInfo>
                  <ItemTitle onClick={() => handleViewDetails(item)}>
                    {item.name}
                  </ItemTitle>
                  <ItemPrice>{parseFloat(item.price).toLocaleString('en-UG')} UGX</ItemPrice>
                  
                  <ItemActions>
                    <ActionButton 
                      primary
                      onClick={() => handleAddToCart(item)}
                      whileTap={{ scale: 0.95 }}
                    >
                      <FaShoppingCart /> Add to Cart
                    </ActionButton>
                  </ItemActions>
                </ItemInfo>
              </WishlistItem>
            ))}
          </AnimatePresence>
        </WishlistGrid>
        
        <div style={{ marginTop: '3rem', textAlign: 'center' }}>
          <ContinueShoppingButton
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/marketplace')}
          >
            <FaArrowLeft /> Continue Shopping
          </ContinueShoppingButton>
        </div>
      </Container>
    </MarketplaceLayout>
  );
};

export default Wishlist;
